/**
 * regional_routes.ts — the inter-settlement ROUTE network (pure, worker-safe).
 *
 * The Unbuilt Town wave: there were no roads between settlements at all —
 * war clashes and treaty stones already sit on the STRAIGHT center-to-center
 * line between neighboring sites, so the routes are exactly those lines
 * (fiction and ground finally agree). Each site connects to its 3 nearest
 * neighbors; edges dedupe by id pair. The route reads on the terrain as a
 * worn dirt track via the roadW mask (no meshes, no collision), and where a
 * route dips under the sea it carries a BRIDGE span (built by
 * HinterlandModule).
 *
 * Pure functions of the seed — every client and worker derives the same
 * network. Spatial bins keep the per-vertex mask query cheap.
 */
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { terrainHeight, riverNear, riverWaterAt } from '../../../shared/terrain.js';
import { commonsSeaLevel } from './sea_level.js';
import { capitalTheta0, nearestCapitalGate, capitalGateExit } from '../../../shared/capitals.js';

export interface RouteSeg { ax: number; az: number; bx: number; bz: number }
export interface BridgeSpan { ax: number; az: number; bx: number; bz: number; /** water surface under the deck (sea level for coastal spans, the river's for a river crossing) */ water: number }

const NEIGHBORS = 3;
/** Track half-width (m) — narrower than any street; a footpath worn by travel. */
export const ROUTE_HALF_W = 1.7;
const ROUTE_FADE = 3.2;
const BIN = 256;

interface RouteNet {
  segs: RouteSeg[];
  bins: Map<string, number[]>;
  bridges: BridgeSpan[];
}

const netCache = new Map<string, RouteNet>();

function binKey(x: number, z: number): string {
  return `${Math.floor(x / BIN)},${Math.floor(z / BIN)}`;
}

function buildNet(seed: string): RouteNet {
  // Districts lie inside their capital — routes leave from the capital's GATES (phase 3).
  const sites = settlementSites(seed).filter((s) => s.tier !== 'district');
  const segs: RouteSeg[] = [];
  const seen = new Set<string>();
  // A capital's endpoint is the exit of the gate nearest the bearing to the other end, never its centre.
  const endpoint = (s: SettlementSite, tx: number, tz: number): { x: number; z: number } => {
    if (s.tier !== 'capital') return { x: s.centerX, z: s.centerZ };
    const th = capitalTheta0(s.siteSeed);
    const ex = capitalGateExit(th, nearestCapitalGate(th, Math.atan2(tz - s.centerZ, tx - s.centerX)));
    return { x: s.centerX + ex.x, z: s.centerZ + ex.z };
  };
  const link = (s: SettlementSite, o: SettlementSite): void => {
    const key = s.id < o.id ? `${s.id}-${o.id}` : `${o.id}-${s.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    const a = endpoint(s, o.centerX, o.centerZ), b = endpoint(o, s.centerX, s.centerZ);
    segs.push({ ax: a.x, az: a.z, bx: b.x, bz: b.z });
  };
  for (const s of sites) {
    const near = sites
      .filter((o) => o.id !== s.id)
      .map((o) => ({ o, d: Math.hypot(o.centerX - s.centerX, o.centerZ - s.centerZ) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, NEIGHBORS);
    for (const { o } of near) link(s, o);
  }
  // Both capitals reach the Grand Circuit's campus explicitly (spec §8).
  const circuit = sites.find((s) => s.tier === 'circuit');
  if (circuit) for (const c of sites) if (c.tier === 'capital') link(c, circuit);
  // Spatial bins: every bin a segment's bbox (+reach) touches lists it.
  const bins = new Map<string, number[]>();
  const reach = ROUTE_HALF_W + ROUTE_FADE + 4;
  segs.forEach((sg, i) => {
    const x0 = Math.min(sg.ax, sg.bx) - reach, x1 = Math.max(sg.ax, sg.bx) + reach;
    const z0 = Math.min(sg.az, sg.bz) - reach, z1 = Math.max(sg.az, sg.bz) + reach;
    for (let bx = Math.floor(x0 / BIN); bx <= Math.floor(x1 / BIN); bx++) {
      for (let bz = Math.floor(z0 / BIN); bz <= Math.floor(z1 / BIN); bz++) {
        const k = `${bx},${bz}`;
        let arr = bins.get(k);
        if (!arr) { arr = []; bins.set(k, arr); }
        arr.push(i);
      }
    }
  });
  // Bridges: contiguous below-sea runs along each route. COARSE-THEN-REFINE
  // (perf law, 2026-08-31): the naive 6 m sweep was ~10M terrainHeight evals
  // over ~577 inter-city segments — a 20-30 s burst that tripped the terrain
  // worker's silence watchdog (worker killed → every chunk built on main).
  // A 240 m coarse pass finds candidate wet windows (a bridgeable crossing
  // is ≥12 m of water — anything that matters shows up within one coarse
  // step of a wet sample); only those windows get the 6 m refine.
  const sea = commonsSeaLevel(seed);
  const bridges: BridgeSpan[] = [];
  const COARSE = 240;
  for (const sg of segs) {
    const L = Math.hypot(sg.bx - sg.ax, sg.bz - sg.az);
    const nc = Math.max(2, Math.ceil(L / COARSE));
    const windows: Array<[number, number]> = [];
    let winStart = -1;
    for (let i = 0; i <= nc; i++) {
      const t = i / nc;
      const px = sg.ax + (sg.bx - sg.ax) * t, pz = sg.az + (sg.bz - sg.az) * t;
      // Sea shallows OR a river's bank zone (terrain wave 2026-09-08): the coarse
      // step is 240 m, a channel 5–80 m, so the refine below also walks every
      // river crossing found by intersecting the route with the network.
      const wetish = terrainHeight(seed, px, pz) < sea + 6 || riverNear(seed, px, pz);
      if (wetish && winStart < 0) winStart = Math.max(0, (i - 1) / nc);
      if ((!wetish || i === nc) && winStart >= 0) {
        windows.push([winStart, Math.min(1, (i + 1) / nc)]);
        winStart = -1;
      }
    }
    // Every point where a river's bank zone touches the route gets a refine
    // window too (24 m walk — riverNear is a hash lookup).
    {
      const nw = Math.max(2, Math.ceil(L / 24));
      let ws = -1;
      for (let i = 0; i <= nw; i++) {
        const t = i / nw;
        const near = riverNear(seed, sg.ax + (sg.bx - sg.ax) * t, sg.az + (sg.bz - sg.az) * t);
        if (near && ws < 0) ws = Math.max(0, (i - 1) / nw);
        if ((!near || i === nw) && ws >= 0) { windows.push([ws, Math.min(1, (i + 1) / nw)]); ws = -1; }
      }
      windows.sort((a, b) => a[0] - b[0]);
    }
    for (const [w0, w1] of windows) {
      const wLen = (w1 - w0) * L;
      const n = Math.max(2, Math.ceil(wLen / 6));
      let runStart = -1;
      let runWater = -Infinity;
      for (let i = 0; i <= n; i++) {
        const t = w0 + (w1 - w0) * (i / n);
        const px = sg.ax + (sg.bx - sg.ax) * t, pz = sg.az + (sg.bz - sg.az) * t;
        const h = terrainHeight(seed, px, pz);
        const rw = riverWaterAt(seed, px, pz);
        const wet = h < sea - 0.4 || (rw !== null && h < rw - 0.2);
        if (wet && runStart < 0) { runStart = t; runWater = -Infinity; }
        if (wet && rw !== null && rw > runWater) runWater = rw;
        if ((!wet || i === n) && runStart >= 0) {
          const runLen = (t - runStart) * L;
          if (runLen >= 6 && runLen <= 320) {
            const m = 4 / L;
            const t0 = Math.max(0, runStart - m);
            const t1 = Math.min(1, t + m);
            bridges.push({
              ax: sg.ax + (sg.bx - sg.ax) * t0, az: sg.az + (sg.bz - sg.az) * t0,
              bx: sg.ax + (sg.bx - sg.ax) * t1, bz: sg.az + (sg.bz - sg.az) * t1,
              water: Number.isFinite(runWater) ? runWater : sea,
            });
          }
          runStart = -1;
        }
      }
    }
  }
  return { segs, bins, bridges };
}

function netFor(seed: string): RouteNet {
  let n = netCache.get(seed);
  if (!n) { n = buildNet(seed); netCache.set(seed, n); }
  return n;
}

/** All route segments (world coords). */
export function routeSegments(seed: string): RouteSeg[] { return netFor(seed).segs; }

/** All bridge spans (world coords). */
export function bridgeSpans(seed: string): BridgeSpan[] { return netFor(seed).bridges; }

/**
 * Worn-track mask 0..1 at a world point for the regional routes — folded into
 * roadSurfaceMask (the roadW bake) at slightly under street strength so the
 * cross-country track reads lighter than a town carriageway.
 */
export function routeMask(seed: string, wx: number, wz: number): number {
  const net = netFor(seed);
  const ids = net.bins.get(binKey(wx, wz));
  if (!ids) return 0;
  let best = 0;
  for (const i of ids) {
    const sg = net.segs[i];
    const dx = sg.bx - sg.ax, dz = sg.bz - sg.az;
    const L2 = dx * dx + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((wx - sg.ax) * dx + (wz - sg.az) * dz) / L2)) : 0;
    const d = Math.hypot(wx - (sg.ax + dx * t), wz - (sg.az + dz * t));
    if (d >= ROUTE_HALF_W + ROUTE_FADE) continue;
    const tt = Math.max(0, Math.min(1, (d - ROUTE_HALF_W) / ROUTE_FADE));
    const m = (1 - tt * tt * (3 - 2 * tt)) * 0.8;
    if (m > best) best = m;
    if (best >= 0.799) break;
  }
  return best;
}

/** Distance to the nearest route line (for field cells to keep off tracks). */
export function distToRoute(seed: string, wx: number, wz: number): number {
  const net = netFor(seed);
  const ids = net.bins.get(binKey(wx, wz));
  if (!ids) return Infinity;
  let best = Infinity;
  for (const i of ids) {
    const sg = net.segs[i];
    const dx = sg.bx - sg.ax, dz = sg.bz - sg.az;
    const L2 = dx * dx + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((wx - sg.ax) * dx + (wz - sg.az) * dz) / L2)) : 0;
    best = Math.min(best, Math.hypot(wx - (sg.ax + dx * t), wz - (sg.az + dz * t)));
  }
  return best;
}
